import { createContext, useContext, useState, useEffect, useCallback , useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

// Session storage keys (cleared when browser closes - more secure than localStorage)
const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

// Use sessionStorage for better security (clears on browser close)
const storage = sessionStorage;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from session storage on mount
  useEffect(() => {
    try {
      const storedToken = storage.getItem(TOKEN_KEY);
      const storedUser = storage.getItem(USER_KEY);
      
      if (storedToken && storedUser) {
        // Check if token is expired
        if (isTokenExpired(storedToken)) {
          console.log('Token expired, clearing auth state');
          storage.removeItem(TOKEN_KEY);
          storage.removeItem(USER_KEY);
        } else {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      }
      
      // Also try to migrate from localStorage if exists (one-time migration)
      const legacyToken = localStorage.getItem(TOKEN_KEY);
      const legacyUser = localStorage.getItem(USER_KEY);
      if (legacyToken && !storedToken) {
        if (!isTokenExpired(legacyToken)) {
          storage.setItem(TOKEN_KEY, legacyToken);
          storage.setItem(USER_KEY, legacyUser);
          setToken(legacyToken);
          setUser(JSON.parse(legacyUser));
        }
        // Clear localStorage after migration
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    } catch (error) {
      console.error('Error reading auth state:', error);
      // Clear corrupted data
      storage.removeItem(TOKEN_KEY);
      storage.removeItem(USER_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback((accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    // Use sessionStorage for better security
    storage.setItem(TOKEN_KEY, accessToken);
    storage.setItem(USER_KEY, JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    storage.removeItem(TOKEN_KEY);
    storage.removeItem(USER_KEY);
    // Also clear any legacy localStorage items
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser((prev) => {
      const next = typeof updatedUser === 'function' ? updatedUser(prev) : updatedUser;
      if (next === prev) return prev;
      storage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const isAuthenticated = Boolean(token && user);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    getAuthHeaders,
  }), [user, token, loading, isAuthenticated, login, logout, updateUser, getAuthHeaders]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
